import { SaneImage } from '@components';
import { Code } from '@components/sanity/serializers/code';
import {
  CodeIcon,
  LockClosedIcon,
  MailIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon
} from '@heroicons/react/outline';
import { PageProps } from '../../../types/page-props';

const code = `import { useSignIn } from "@clerk/clerk-react";

function MagicLinks() {
  const { signIn } = useSignIn();

  const {
    startMagicLinkFlow,
    cancelMagicLinkFlow,
  } = signIn.createMagicLinkFlow();

  const onClick = () => {
    startMagicLinkFlow({
      redirect_url: '...'
    })
  }

  return (
    <form>
      <input type='email' name='email'/>
      <button onClick={onClick}>
        Send magic link
      </button>
    </form>
  )
}`;

const userFeatures = [
  {
    id: 1,
    name: 'Seamless Sign-up & Sign-in',
    description: `Authenticate users from anywhere in your site, while allowing one-click magic link's to be seamlessly completed from any device.`,
    icon: SparklesIcon
  },
  {
    id: 2,
    name: 'Protect your product and user data',
    description:
      'Eliminate bot attacks, account takeovers, and the risk of passwords.',
    icon: ShieldCheckIcon
  },
  {
    id: 3,
    name: 'Ensure accounts are tied to a single user',
    description:
      'Magic links ensure that each account is tied to a verified email account.',
    icon: UsersIcon
  }
];

const developerFeatures = [
  {
    id: 1,
    name: 'Flexible integration and implementation',
    description:
      'Implement fully-fledged magic links in minutes, then, customize as much as you want with powerful frontend and backend APIs.',
    icon: CodeIcon
  },
  {
    id: 2,
    name: 'MFA supported out-of-the-box',
    description:
      'Combine magic links with multi-factor authentication. Our magic links work seamlessly with our MFA functionality, all implemented in minutes.',
    icon: LockClosedIcon
  },
  {
    id: 3,
    name: 'Deliverability you can count on',
    description:
      'Stop worrying about complex configurations and multi-level dependencies with email and SMS providers. Clerk ensures verification emails are speedy with exceptionally high deliverability.',
    icon: MailIcon
  }
];

export function UserAndDeveloperFeatures({ image }: PageProps): JSX.Element {
  return (
    <div className="overflow-hidden py-16 lg:py-24">
      <div className="relative mx-auto max-w-xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
        <div className="relative">
          <h2 className="text-center text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
            Onboarding that your users will love
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-center text-xl text-gray-500">
            Use Clerk&apos;s{' '}
            <span className="font-semibold text-gray-600">
              magic link flows
            </span>{' '}
            to boost user metrics and eliminate security risks. Quickly verify
            your users in one step to improve conversion rates, while filtering
            out spam and fraud.
          </p>
        </div>

        <div className="relative mt-4 lg:mt-8 lg:grid lg:grid-cols-2 lg:items-center lg:gap-8">
          <div className="relative">
            <dl className="space-y-10">
              {userFeatures.map(item => (
                <div key={item.id} className="relative">
                  <dt>
                    <div className="absolute flex h-12 w-12 items-center justify-center rounded-md bg-clerk-blue text-white">
                      <item.icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <p className="ml-16 text-lg font-medium leading-6 text-gray-900">
                      {item.name}
                    </p>
                  </dt>
                  <dd className="mt-2 ml-16 text-base text-gray-500">
                    {item.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="relative -mx-4 mt-10 lg:mt-0" aria-hidden="true">
            <div className="drop-shadow-clerk-components filter">
              <SaneImage src={image.verify} className="mx-auto" />
            </div>
          </div>
        </div>

        <div className="relative mt-12 sm:mt-16 lg:mt-24">
          <div className="lg:grid lg:grid-flow-row-dense lg:grid-cols-2 lg:items-center lg:gap-8">
            <div className="lg:col-start-2">
              <h3 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
                Tools your developers will love
              </h3>
              <p className="mt-3 text-lg text-gray-500">
                Enjoy a first-class developer experience, customizable flows,
                and robust out-of-the-box functionality
              </p>

              <dl className="mt-10 space-y-10">
                {developerFeatures.map(item => (
                  <div key={item.id} className="relative">
                    <dt>
                      <div className="absolute flex h-12 w-12 items-center justify-center rounded-md bg-clerk-blue text-white">
                        <item.icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="ml-16 text-lg font-medium leading-6 text-gray-900">
                        {item.name}
                      </p>
                    </dt>
                    <dd className="mt-2 ml-16 text-base text-gray-500">
                      {item.description}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="relative -mx-4 mt-10 px-4 md:px-0 lg:col-start-1 lg:mt-0">
              <div className="relative sm:mt-16 lg:m-8">
                <Code language="jsx" theme="dark" highlightedLines={undefined}>
                  {code}
                </Code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
